/**
 * 發票載具自動登錄 — 授權端點的回應碼（`receiptCarrierBind.yaml`／`receiptCarrierRenewBySponsor.yaml`）
 *
 * ⚠️ 只放後端語意，**code → 文案的對應留在各自的 hook**：同一個 code 在兩支端點語意不同
 * （`RCA-V0-FF1` 活動層是「這檔活動未開自動登錄」、sponsor 層是「品牌已無仍在收的活動」），
 * 合成一張文案表一定會寫錯其中一邊。
 */
export const receiptCarrierCodes = {
    /** **唯一**能對會員說「驗證碼錯誤」的 code */
    CODE_WRONG: 'RCA-3RD-F0',
    /**
     * ⚠️ 一碼兩義、處置相反，只能靠 `message` 分辨，所以下游要走 `resolveCarrierErrCode()`：
     *   - `...did not respond.` ＝ 財政部端逾時，**重試有機會成功**
     *   - `...is busy.` ＝ 送出前就被我方擋下（同組條碼查太多次／當日額度用完），重試必失敗
     */
    THIRD_PARTY_TIMEOUT: 'RCA-3RD-FT0',
    /** 條碼已被其他會員綁定。⚠️ 會累加會員總嘗試次數 */
    BOUND_BY_OTHERS: 'RCA-V0-FD0',
    /** 帶的 `cardNo` 與授權列不同。前端一律不帶 cardNo，正常碰不到 */
    CARD_NO_MISMATCH: 'RCA-C0-FF1',
    /** 送的是續期，但這位會員在本 Partner 已無授權 */
    NO_AUTHORIZATION: 'RCA-U0-FNF1',
    /** 驗證碼已失效，這次請求要補 `cardEncrypt` */
    NEED_CARD_ENCRYPT: 'RCA-U0-FRPE',
    /**
     * ⚠️ 一碼三義（見檔頭）。活動層的兩種目前共用同一句文案——可以這樣是因為編輯器只開得出
     * facebook／line／enterprise，而這三種後端都支援，所以「登入方式不支援」碰不到。
     */
    AUTO_ENTRY_UNAVAILABLE: 'RCA-V0-FF1',
    /** 活動已上線但查不到起訖日。⚠️ 會消耗一次限速額度 */
    PROMO_DATE_MISSING: 'RCA-C0-F0',
    /** 422 欄位驗證失敗——前端該先擋住，走到這裡＝我們漏擋 */
    FIELD_VALIDATION_FAILED: 'RCA-V0-F0',
    /** 409 併發（會員層的鎖）。**這個重試就會好**，是唯一能說「請稍後再試」的 */
    CONCURRENT: 'RCA-U0-FD0',
    /** 限速，擋在呼叫財政部之前。`RCA-3RD-FT0` 的 busy 變體也正規化成這個 */
    THROTTLED: 'RCA-AU0-FF1',
    SPONSOR_NOT_IN_PARTNER: 'RCA-R0-FF1',
    PARTNER_NOT_FOUND: 'PT-R1-FNF1',
    MISSING_FEVER_HOST: 'CN-V0-F0',
    PROMO_OVER: 'CUDF-V0-F0',
    /** 活動已停用或暫停中 */
    PROMO_DISABLED: 'PM-V0-F0',
    /**
     * token 查不到登入狀態。**是 400 但重試不會好**，API 層比照 401 丟出。
     * ⚠️ `errorObjectChecker.js` 的 `isFeverApiAccessTokenExpired` 判的是同一個碼（小寫）。
     */
    ACCESS_TOKEN_NOT_FOUND: 'MAJ-R1-FU1'
};

/** 只有「我們的假設錯了」才送 Sentry；會員自己修得好的錯誤送出去會淹掉真正的故障 */
export const RECEIPT_CARRIER_SENTRY_CODES = new Set([
    receiptCarrierCodes.FIELD_VALIDATION_FAILED,
    receiptCarrierCodes.NO_AUTHORIZATION,
    receiptCarrierCodes.MISSING_FEVER_HOST
]);

/** 「手上那份 GET 過時了」——兩個都要重讀，差別只在出不出聲 */
export const RECEIPT_CARRIER_STALE_CODES = {
    SILENT: receiptCarrierCodes.NEED_CARD_ENCRYPT,
    NOISY: receiptCarrierCodes.NO_AUTHORIZATION
};

const THIRD_PARTY_BUSY_MESSAGE = 'Carrier verification service is busy';

export const isCarrierThrottledByCardNo = (payload = {}) =>
    payload?.code === receiptCarrierCodes.THIRD_PARTY_TIMEOUT &&
    payload?.message?.trim().replace(/\.$/, '') === THIRD_PARTY_BUSY_MESSAGE;

/**
 * 取這次失敗的 code，**下游一律用這支、不要直接讀 `payload.code`**：
 * 把 `RCA-3RD-FT0` 的限速變體正規化成 `THROTTLED`，讓限速在前端只有一個出口。
 */
export function resolveCarrierErrCode(payload = {}) {
    return isCarrierThrottledByCardNo(payload) ? receiptCarrierCodes.THROTTLED : payload?.code;
}
